import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/TicketsPage.css";
import { createTicket, getTicketCategories, getTickets } from "../api/ticket";

const statusLabels = {
  customer_response: "پاسخ شما",
  support_response: "پاسخ پشتیبانی",
  under_review: "در حال بررسی",
  closed: "بسته شده",
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fa-IR");
};

const TicketsPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalError, setModalError] = useState("");
  const [form, setForm] = useState({
    subject: "",
    category_id: "",
    account_number: "",
    message: "",
  });
  const navigate = useNavigate();

  const statusFilter = activeTab === "closed" ? "closed" : "";

  const rows = useMemo(
    () =>
      tickets.map((t) => ({
        id: t.id,
        number: t.ticket_number || t.id,
        date: formatDate(t.created_at),
        account: t.account_number || "-",
        step: t.subject || t.category?.name || "-",
        status: statusLabels[t.status] || t.status,
      })),
    [tickets]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ticketRes, categoryRes] = await Promise.allSettled([
        getTickets(statusFilter),
        getTicketCategories(),
      ]);
      if (ticketRes.status === "fulfilled") {
        setTickets(Array.isArray(ticketRes.value) ? ticketRes.value : []);
      } else {
        setError(ticketRes.reason?.message || "خطا در دریافت تیکت ها");
      }
      if (categoryRes.status === "fulfilled") {
        setCategories(Array.isArray(categoryRes.value) ? categoryRes.value : []);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async () => {
    if (!form.subject || !form.account_number) {
      setModalError("عنوان تیکت و شماره حساب الزامی است.");
      return;
    }
    setSubmitLoading(true);
    setError("");
    setMessage("");
    setModalError("");
    try {
      await createTicket({
        subject: form.subject,
        account_number: form.account_number,
        category_id: form.category_id || null,
        message: form.message || "",
      });
      setMessage("تیکت با موفقیت ثبت شد.");
      setShowModal(false);
      setForm({ subject: "", category_id: "", account_number: "", message: "" });
      if (activeTab === "open") {
        loadData();
      } else {
        setActiveTab("open");
      }
    } catch (err) {
      setModalError(err.message || "خطا در ثبت تیکت");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MenuLayout>
      <div className="tickets-page">
        <header className="tickets-header">
          <h1 className="tickets-title">تیکت ها</h1>
          <p className="tickets-subtitle">
            برای پیگیری سوالات و مشکلات خود، تیکت ثبت کنید و وضعیت آن را مشاهده کنید.
          </p>
        </header>

        <div className="tickets-actions">
          <button className="tickets-new" onClick={() => setShowModal(true)}>
            افزودن تیکت جدید +
          </button>
          <div className="tickets-tabs">
            <button
              className={`tickets-tab ${activeTab === "open" ? "tickets-tab-active" : ""}`}
              onClick={() => setActiveTab("open")}
            >
              تیکت های باز
            </button>
            <button
              className={`tickets-tab ${activeTab === "closed" ? "tickets-tab-active" : ""}`}
              onClick={() => setActiveTab("closed")}
            >
              تیکت های بسته شده
            </button>
          </div>
        </div>

        {error && <div className="tickets-error">{error}</div>}
        {message && <div className="tickets-success">{message}</div>}

        <div className="tickets-table">
          <div className="tickets-head">
            <span>شماره</span>
            <span>تاریخ ثبت:</span>
            <span>شماره حساب:</span>
            <span>عنوان تیکت:</span>
            <span>وضعیت:</span>
            <span></span>
          </div>
          <div className="tickets-body">
            {loading ? (
              <div className="tickets-row">در حال دریافت...</div>
            ) : rows.length === 0 ? (
              <div className="tickets-row">تیکتی یافت نشد.</div>
            ) : (
              rows.map((t) => (
                <div className="tickets-row" key={t.id}>
                  <span>{t.number}</span>
                  <span>{t.date}</span>
                  <span>{t.account}</span>
                  <span>{t.step}</span>
                  <span className="tickets-status">{t.status}</span>
                  <span>
                    <button
                      className={`tickets-detail-btn ${t.id === rows[0]?.id ? "tickets-detail-btn-primary" : ""}`}
                      onClick={() => navigate(`/tickets/${t.id}`)}
                    >
                      مشاهده تیکت
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {showModal && (
          <div className="ticket-modal-backdrop" onClick={() => setShowModal(false)}>
            <div className="ticket-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="ticket-modal-title">افزودن تیکت جدید</h2>
              {modalError && <div className="ticket-modal-error">{modalError}</div>}
              <label className="ticket-modal-label">
                عنوان تیکت :
                <input
                  className="ticket-modal-input"
                  type="text"
                  placeholder="عنوان تیکت"
                  value={form.subject}
                  onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                />
              </label>
              <label className="ticket-modal-label">
                شماره حساب :
                <input
                  className="ticket-modal-input"
                  type="text"
                  placeholder="شماره حساب"
                  value={form.account_number}
                  onChange={(e) => setForm((prev) => ({ ...prev, account_number: e.target.value }))}
                />
              </label>
              <label className="ticket-modal-label">
                دسته بندی :
                <select
                  className="ticket-modal-input"
                  value={form.category_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, category_id: e.target.value }))}
                >
                  <option value="">انتخاب دسته بندی</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ticket-modal-label">
                پیام :
                <textarea
                  className="ticket-modal-textarea"
                  placeholder="پیام خود را بنویسید"
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                ></textarea>
              </label>
              <div className="ticket-modal-actions">
                <button className="ticket-modal-submit" onClick={handleSubmit} disabled={submitLoading}>
                  {submitLoading ? "در حال ارسال..." : "ارسال"}
                </button>
                <button className="ticket-modal-close" onClick={() => setShowModal(false)}>
                  بستن
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MenuLayout>
  );
};

export default TicketsPage;
